// src/pages/Dealer/EditDealer.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import dealerService from '../../services/dealer.service';
import Toast from '../../components/Toast/Toast';
import employeeService from '../../services/employee.service';
import geoService from '../../services/geo.service';
import '../../styles/AddPages.css';

const ADMIN_ROLES = ['SUPER_ADMIN', 'OFFICE_ADMIN'];

const EditDealer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const userRoleName = user?.role?.name || user?.role || '';
  const isAdmin = ADMIN_ROLES.includes(userRoleName);

  const [toast,        setToast]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors,       setErrors]       = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);

  const [regions,         setRegions]         = useState([]);
  const [headquarters,    setHeadquarters]    = useState([]);
  const [subHeadquarters, setSubHeadquarters] = useState([]);
  const [loadingRegions,  setLoadingRegions]  = useState(false);
  const [loadingHQs,      setLoadingHQs]      = useState(false);
  const [loadingSubHQs,   setLoadingSubHQs]   = useState(false);

  // Geo cascade
  const [states,    setStates]    = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas,   setTalukas]   = useState([]);

  const [loadingStates,    setLoadingStates]    = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas,   setLoadingTalukas]   = useState(false);

  // ✅ User-change guards — prevent cascade effects from wiping pre-loaded values
  const [regionChangedByUser,   setRegionChangedByUser]   = useState(false);
  const [hqChangedByUser,       setHqChangedByUser]       = useState(false);
  const [stateChangedByUser,    setStateChangedByUser]    = useState(false);
  const [districtChangedByUser, setDistrictChangedByUser] = useState(false);

  const [latLongLockedFromDB, setLatLongLockedFromDB] = useState(false);
  const latLongLocked       = !isAdmin && latLongLockedFromDB;
  const otherFieldsDisabled = !isAdmin;

  const [formData, setFormData] = useState({
    photo: null,
    latitude: '',
    longitude: '',
    regionId: '',
    headquarterId: '',
    subHeadquarterId: '',
    dealerShopName: '',
    ownerName: '',
    gstNumber: '',
    panNumber: '',
    aadharCardNumber: '',
    dateOfBirth: '',
    address: '',
    state: '',
    district: '',
    tahsil: '',
    mobile: '',
    email: '',
    remark: '',
  });

  // ── Initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        setLoadingRegions(true);
        setLoadingStates(true);

        const [dealerRes, regionsRes, statesRes] = await Promise.all([
          dealerService.getDealerById(id),
          employeeService.getAllRegions(),
          geoService.getStates(),
        ]);

        if (regionsRes.success) setRegions(regionsRes.data || []);
        if (statesRes.success)  setStates(statesRes.data || []);

        if (dealerRes.success) {
          const d = dealerRes.data?.data || dealerRes.data;
          setLatLongLockedFromDB(!!d.latLongUpdatedByNonAdmin);

          setFormData({
            photo: null,
            latitude:         d.latitude         || '',
            longitude:        d.longitude        || '',
            regionId:         d.regionId         ? String(d.regionId)         : '',
            headquarterId:    d.headquarterId    ? String(d.headquarterId)    : '',
            subHeadquarterId: d.subHeadquarterId ? String(d.subHeadquarterId) : '',
            dealerShopName:   d.dealerShopName   || '',
            ownerName:        d.ownerName        || '',
            gstNumber:        d.gstNumber        || '',
            panNumber:        d.panNumber        || '',
            aadharCardNumber: d.aadharCardNumber || '',
            dateOfBirth: (() => {
              if (!d.dateOfBirth) return '';
              const date = new Date(d.dateOfBirth);
              return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
            })(),
            address:  d.address  || '',
            state:    d.state    || '',
            district: d.district || '',
            tahsil:   d.tahsil   || '',
            mobile:   d.mobile   || '',
            email:    d.email    || '',
            remark:   d.remark   || '',
          });
          if (d.photo) setPhotoPreview(d.photo);

          // Pre-load HQs for current region
          if (d.regionId) {
            setLoadingHQs(true);
            const hqRes = await employeeService.getHeadquartersByRegion(d.regionId);
            if (hqRes.success) setHeadquarters(hqRes.data || []);
            setLoadingHQs(false);
          }

          // Pre-load Sub-HQs for current HQ
          if (d.headquarterId) {
            setLoadingSubHQs(true);
            const subHqRes = await employeeService.getSubHeadquartersByHQ(d.headquarterId);
            if (subHqRes.success) setSubHeadquarters(subHqRes.data || []);
            setLoadingSubHQs(false);
          }

          // Pre-load district list for current state
          if (d.state) {
            const dRes = await geoService.getDistricts(d.state);
            if (dRes.success) setDistricts(dRes.data || []);
          }

          // Pre-load taluka list for current district
          if (d.state && d.district) {
            const tRes = await geoService.getTalukas(d.state, d.district);
            if (tRes.success) setTalukas(tRes.data || []);
          }
        }
      } catch (error) {
        console.error('Fetch dealer error:', error);
        setToast({ message: 'Failed to load dealer data', type: 'error' });
      } finally {
        setLoading(false);
        setLoadingRegions(false);
        setLoadingStates(false);
      }
    };
    init();
  }, [id]);

  // ── Region change (user-driven only) → reload HQs ────────────────────────
  useEffect(() => {
    if (!regionChangedByUser) return;
    setFormData(prev => ({ ...prev, headquarterId: '', subHeadquarterId: '' }));
    setHeadquarters([]);
    setSubHeadquarters([]);
    if (!formData.regionId) return;

    setLoadingHQs(true);
    employeeService.getHeadquartersByRegion(formData.regionId)
      .then(res => { if (res.success) setHeadquarters(res.data || []); })
      .finally(() => setLoadingHQs(false));
  }, [formData.regionId, regionChangedByUser]);

  // ── HQ change (user-driven only) → reload Sub-HQs ────────────────────────
  useEffect(() => {
    if (!hqChangedByUser) return;
    setFormData(prev => ({ ...prev, subHeadquarterId: '' }));
    setSubHeadquarters([]);
    if (!formData.headquarterId) return;

    setLoadingSubHQs(true);
    employeeService.getSubHeadquartersByHQ(formData.headquarterId)
      .then(res => { if (res.success) setSubHeadquarters(res.data || []); })
      .finally(() => setLoadingSubHQs(false));
  }, [formData.headquarterId, hqChangedByUser]);

  // ── State change (user-driven only) → reload districts ───────────────────
  useEffect(() => {
    if (!stateChangedByUser) return;
    setDistricts([]); setTalukas([]);
    if (!formData.state) return;
    setLoadingDistricts(true);
    geoService.getDistricts(formData.state)
      .then(r => { if (r.success) setDistricts(r.data || []); })
      .finally(() => setLoadingDistricts(false));
  }, [formData.state, stateChangedByUser]);

  // ── District change (user-driven only) → reload talukas ──────────────────
  useEffect(() => {
    if (!districtChangedByUser) return;
    setTalukas([]);
    if (!formData.state || !formData.district) return;
    setLoadingTalukas(true);
    geoService.getTalukas(formData.state, formData.district)
      .then(r => { if (r.success) setTalukas(r.data || []); })
      .finally(() => setLoadingTalukas(false));
  }, [formData.district, districtChangedByUser]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if ((name === 'latitude' || name === 'longitude') && latLongLocked) return;
    if (otherFieldsDisabled && name !== 'latitude' && name !== 'longitude') return;

    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (name === 'regionId')      setRegionChangedByUser(true);
      if (name === 'headquarterId') setHqChangedByUser(true);
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const detectLocation = () => {
  if (!navigator.geolocation) {
    setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
    return;
  }

  setToast({ message: 'Detecting location…', type: 'info' });

  let bestReading = null;
  let settled = false;

  const finish = (position) => {
    if (settled) return;
    settled = true;
    navigator.geolocation.clearWatch(watchId);

    const { latitude, longitude, accuracy } = position.coords;
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toFixed(8),
      longitude: longitude.toFixed(8),
      locationAccuracy: accuracy, // store this on the entity too
    }));

    setToast({ message: '📍 Location detected!', type: 'success' });
  };

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
        bestReading = position;
      }
      // good enough — stop early
      if (position.coords.accuracy <= 15) {
        finish(position);
      }
    },
    (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      const message =
        error.code === error.PERMISSION_DENIED
          ? 'Location access denied. Please enable it in your browser/app settings.'
          : error.code === error.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Unable to detect location.';
      setToast({ message, type: 'error' });
      console.error('Geolocation error:', error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );

  // Hard stop after ~12s — use best reading we've got rather than hang forever
  setTimeout(() => {
    if (!settled && bestReading) {
      finish(bestReading);
    } else if (!settled) {
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      setToast({ message: 'Could not get an accurate location. ', type: 'error' });
    }
  }, 12000);
};

  const validate = () => {
    if (!isAdmin) {
      const newErrors = {};
      if (!formData.latitude || !formData.longitude) {
        newErrors.latitude = 'Both latitude and longitude are required';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    const newErrors = {};
    if (!formData.dealerShopName.trim()) newErrors.dealerShopName = 'Dealer shop name is required';
    if (!formData.ownerName.trim())      newErrors.ownerName      = 'Owner name is required';
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be 10 digits';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Invalid email format';
    if (formData.gstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber))
      newErrors.gstNumber = 'Invalid GST number format';
    if (formData.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber))
      newErrors.panNumber = 'Invalid PAN number format';
    if (formData.aadharCardNumber && !/^\d{12}$/.test(formData.aadharCardNumber))
      newErrors.aadharCardNumber = 'Aadhar number must be 12 digits';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (latLongLocked) {
      setToast({ message: 'GPS location is already locked and cannot be changed', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      let dataToSend;

      if (isAdmin) {
        dataToSend = { ...formData };
        if (isSuperAdmin) {
          const selectedCompanyId = localStorage.getItem('selectedCompanyId');
          if (!selectedCompanyId) {
            setToast({ message: 'Please select a company first from the navbar', type: 'error' });
            setIsSubmitting(false);
            return;
          }
          dataToSend.companyId = parseInt(selectedCompanyId);
        }
        if (formData.photo) dataToSend.photo = photoPreview;
        else delete dataToSend.photo;
      } else {
        dataToSend = {
          latitude:  formData.latitude,
          longitude: formData.longitude,
        };
      }

      const response = await dealerService.updateDealer(id, dataToSend);

      if (response.success) {
        setToast({
          message: isAdmin
            ? 'Dealer updated successfully!'
            : 'GPS location updated successfully! It is now locked.',
          type: 'success',
        });
        setTimeout(() => navigate('/dealer/view'), 1500);
      }
    } catch (error) {
      setToast({ message: error.response?.data?.message || 'Failed to update dealer', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="add-dealer-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading dealer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="add-dealer-page">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/dealer/view">Dealers</a></li>
          <li className="breadcrumb-item active">Edit Dealer</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Edit Dealer</h4>
        {!isAdmin && (
          <p style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>
            ℹ️ You can only update the GPS location for this dealer.
            {latLongLockedFromDB
              ? ' Location has already been updated once and cannot be changed again.'
              : ' You can update the location ONCE — after that it will be locked.'}
          </p>
        )}
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>

          {/* Photo & Location */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Dealer Photo</label>
                <input type="file" name="photo" className="form-input"
                  onChange={handleChange} accept="image/*"
                  disabled={otherFieldsDisabled}/>
                {photoPreview && (
                  <div className="photo-preview-large">
                    <img src={photoPreview} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Latitude
                  {latLongLocked && <span style={{ color: '#c62828', fontSize: 11, marginLeft: 6 }}>🔒 Locked</span>}
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input type="text" name="latitude" className="form-input"
                    value={formData.latitude} onChange={handleChange}
                    placeholder="Enter latitude"
                    style={{ flex: 1, minWidth: 200 }}
                    disabled={latLongLocked}/>
                  <button type="button" className="btn btn-secondary"
                    onClick={detectLocation} disabled={latLongLocked}
                    style={{ flexShrink: 0 }}>
                    📍 Detect
                  </button>
                </div>
                {errors.latitude && <span className="error-message">{errors.latitude}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Longitude
                  {latLongLocked && <span style={{ color: '#c62828', fontSize: 11, marginLeft: 6 }}>🔒 Locked</span>}
                </label>
                <input type="text" name="longitude" className="form-input"
                  value={formData.longitude} onChange={handleChange}
                  placeholder="Enter longitude" disabled={latLongLocked}/>
              </div>
            </div>
          </div>

          {/* Area Assignment */}
          <div className="form-section">
            <h3 className="section-title">📍 Area Assignment</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Region</label>
                {loadingRegions ? (
                  <div className="form-input" style={{ color: '#adb5bd' }}>Loading regions...</div>
                ) : (
                  <select name="regionId" className="form-input"
                    value={formData.regionId} onChange={handleChange}
                    disabled={otherFieldsDisabled}>
                    <option value="">Select Region</option>
                    {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Headquarter</label>
                {loadingHQs ? (
                  <div className="form-input" style={{ color: '#adb5bd' }}>Loading headquarters...</div>
                ) : (
                  <select name="headquarterId" className="form-input"
                    value={formData.headquarterId} onChange={handleChange}
                    disabled={otherFieldsDisabled || !formData.regionId}>
                    <option value="">
                      {!formData.regionId ? 'Select Region first' : 'Select Headquarter'}
                    </option>
                    {headquarters.map(hq => <option key={hq.id} value={hq.id}>{hq.name}</option>)}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Sub-Headquarter</label>
                {loadingSubHQs ? (
                  <div className="form-input" style={{ color: '#adb5bd' }}>Loading sub-headquarters...</div>
                ) : (
                  <select name="subHeadquarterId" className="form-input"
                    value={formData.subHeadquarterId} onChange={handleChange}
                    disabled={otherFieldsDisabled || !formData.headquarterId}>
                    <option value="">
                      {!formData.headquarterId
                        ? 'Select Headquarter first'
                        : subHeadquarters.length === 0
                          ? 'No sub-headquarters available'
                          : 'Select Sub-Headquarter (optional)'}
                    </option>
                    {subHeadquarters.map(sh => <option key={sh.id} value={sh.id}>{sh.name}</option>)}
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Dealer Shop Name <span className="required">*</span></label>
                <input type="text" name="dealerShopName"
                  className={`form-input ${errors.dealerShopName ? 'error' : ''}`}
                  value={formData.dealerShopName} onChange={handleChange}
                  placeholder="Enter dealer shop name" disabled={otherFieldsDisabled}/>
                {errors.dealerShopName && <span className="error-message">{errors.dealerShopName}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Owner Name <span className="required">*</span></label>
                <input type="text" name="ownerName"
                  className={`form-input ${errors.ownerName ? 'error' : ''}`}
                  value={formData.ownerName} onChange={handleChange}
                  placeholder="Enter owner name" disabled={otherFieldsDisabled}/>
                {errors.ownerName && <span className="error-message">{errors.ownerName}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input type="date" name="dateOfBirth" className="form-input"
                  value={formData.dateOfBirth} onChange={handleChange}
                  disabled={otherFieldsDisabled}/>
              </div>
            </div>
          </div>

          {/* Tax & Identity */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">GST Number</label>
                <input type="text" name="gstNumber"
                  className={`form-input ${errors.gstNumber ? 'error' : ''}`}
                  value={formData.gstNumber} onChange={handleChange}
                  placeholder="Enter GST number" maxLength="15"
                  disabled={otherFieldsDisabled}/>
                {errors.gstNumber && <span className="error-message">{errors.gstNumber}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">PAN Number</label>
                <input type="text" name="panNumber"
                  className={`form-input ${errors.panNumber ? 'error' : ''}`}
                  value={formData.panNumber} onChange={handleChange}
                  placeholder="Enter PAN number" maxLength="10"
                  disabled={otherFieldsDisabled}/>
                {errors.panNumber && <span className="error-message">{errors.panNumber}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Aadhar Card Number</label>
                <input type="text" name="aadharCardNumber"
                  className={`form-input ${errors.aadharCardNumber ? 'error' : ''}`}
                  value={formData.aadharCardNumber} onChange={handleChange}
                  placeholder="Enter 12-digit Aadhar" maxLength="12"
                  disabled={otherFieldsDisabled}/>
                {errors.aadharCardNumber && <span className="error-message">{errors.aadharCardNumber}</span>}
              </div>
            </div>
          </div>

          {/* Address — cascade dropdowns */}
          <div className="form-section">
            <h3 className="section-title">📍 Address</h3>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">State</label>
                {loadingStates ? (
                  <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
                ) : (
                  <select name="state" className="form-input"
                    value={formData.state}
                    onChange={(e) => {
                      setStateChangedByUser(true);
                      setDistrictChangedByUser(false);
                      setFormData(f => ({
                        ...f, state: e.target.value, district: '', tahsil: '',
                      }));
                    }}
                    disabled={otherFieldsDisabled}>
                    <option value="">Select State</option>
                    {states.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">District</label>
                {loadingDistricts ? (
                  <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
                ) : (
                  <select name="district" className="form-input"
                    value={formData.district}
                    onChange={(e) => {
                      setDistrictChangedByUser(true);
                      setFormData(f => ({
                        ...f, district: e.target.value, tahsil: '',
                      }));
                    }}
                    disabled={otherFieldsDisabled || !formData.state}>
                    <option value="">
                      {!formData.state ? 'Select State first' : 'Select District'}
                    </option>
                    {districts.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Tahsil (Taluka)</label>
                {loadingTalukas ? (
                  <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
                ) : (
                  <select name="tahsil" className="form-input"
                    value={formData.tahsil}
                    onChange={(e) => setFormData(f => ({ ...f, tahsil: e.target.value }))}
                    disabled={otherFieldsDisabled || !formData.district}>
                    <option value="">
                      {!formData.district ? 'Select District first' : 'Select Tahsil'}
                    </option>
                    {talukas.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Address (Street / Landmark)</label>
                <textarea name="address" className="form-textarea"
                  value={formData.address} onChange={handleChange}
                  placeholder="Shop number, street, landmark..." rows="2"
                  disabled={otherFieldsDisabled}/>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Mobile <span className="required">*</span></label>
                <input type="tel" name="mobile"
                  className={`form-input ${errors.mobile ? 'error' : ''}`}
                  value={formData.mobile} onChange={handleChange}
                  placeholder="Enter 10-digit mobile" maxLength="10"
                  disabled={otherFieldsDisabled}/>
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Email ID</label>
                <input type="email" name="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  value={formData.email} onChange={handleChange}
                  placeholder="Enter email address"
                  disabled={otherFieldsDisabled}/>
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Remark</label>
                <textarea name="remark" className="form-textarea"
                  value={formData.remark} onChange={handleChange}
                  placeholder="Enter any additional remarks" rows="3"
                  disabled={otherFieldsDisabled}/>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/dealer/view')} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary"
              disabled={isSubmitting || (!isAdmin && latLongLocked)}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDealer;